<template>
  <div class="bf-card bf-chart-card">
    <div class="bf-chart-card__head">
      <div class="bf-chart-card__title">Registros por integração</div>
      <div class="bf-chart-card__subtitle">% do total no período</div>
    </div>

    <div class="chart activeCoursesChart">
      <DoughnutChart :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed, onMounted } from "vue";
import { Doughnut } from "vue-chartjs";
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from "chart.js";
import type { DashboardResponse } from "@/types/response/dashboard";

// Registrar os componentes do Chart.js
ChartJS.register(Title, Tooltip, Legend, ArcElement);

export default defineComponent({
  name: "LineDivisionByIntegration",
  components: {
    DoughnutChart: Doughnut,
  },
  props: {
    isLoading: {
      type: Boolean,
      required: false,
    },
    data: {
      type: Object as () => DashboardResponse,
      required: true,
    },
  },
  setup(props) {
    const seriesData = ref<number[]>([]);
    const labels = ref<string[]>([]);
    const colors = ref<string[]>([]);
    const legendPosition = ref("right");

    const updateChartData = () => {
      if (!props.data || !props.data.success_lines_per_connector) return;

      const totalLines = Object.values(
        props.data.success_lines_per_connector
      ).reduce((sum, value) => sum + (value || 0), 0);

      seriesData.value = Object.values(
        props.data.success_lines_per_connector
      ).map((value) => ((value || 0) / totalLines) * 100);

      labels.value = Object.keys(props.data.success_lines_per_connector);

      // Paleta roxa do design system (cicla quando há mais conectores que cores)
      const palette = [
        "#4a0a85",
        "#7c16e0",
        "#9b2bff",
        "#b866ff",
        "#d0a3ff",
        "#5f1aa8",
        "#c98cff",
      ];

      colors.value = labels.value.map(
        (_, index) => palette[index % palette.length]
      );
    };

    watch(
      () => props.data,
      () => {
        updateChartData();
      },
      { immediate: true }
    );

    const updateLegendPosition = () => {
      legendPosition.value = window.innerWidth <= 768 ? "top" : "right";
    };

    onMounted(() => {
      updateLegendPosition();
      window.addEventListener("resize", updateLegendPosition);
    });

    const chartData = computed(() => ({
      labels: labels.value,
      datasets: [
        {
          data: seriesData.value,
          backgroundColor: colors.value,
          borderWidth: 0,
        },
      ],
    }));

    const chartOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: -40,
          bottom: -40,
        },
      },
      plugins: {
        legend: {
          // position: "left" as const,
          position:
            (legendPosition.value.toString() as "right") ||
            "top" ||
            "left" ||
            "bottom" ||
            "chartArea",
          display: false,
          labels: {
            color: "#5B5B98",
            font: {
              size: 9,
            },
            // Adiciona percentual ao lado do nome de cada label
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            generateLabels(chart: any) {
              return chart.data.labels?.map((label: string, i: number) => {
                const value = seriesData.value[i];
                return {
                  text: `${label}: ${value.toFixed(2)}%`,
                  fillStyle: colors.value[i],
                  hidden: chart.getDatasetMeta(0).data[i].hidden,
                };
              });
            },
          },
        },
        tooltip: {
          callbacks: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            label: (context: any) =>
              `${context.label}: ${context.raw.toFixed(2)}%`,
          },
        },
      },
    }));

    return {
      chartData,
      chartOptions,
    };
  },
});
</script>

<style lang="scss" scoped>
.bf-chart-card {
  padding: 20px 22px;

  &__title {
    font-size: 14px;
    font-weight: 700;
    color: var(--text);
  }

  &__subtitle {
    font-size: 11.5px;
    color: var(--text3);
    margin-top: 2px;
  }
}

.chart {
  position: relative;
  margin-top: 16px;
  height: 300px;
}
</style>
